import { Injectable } from '@angular/core';
import { InventoryQueryParamsService } from 'src/app/models/interfaces/inventory-query-params-service';
import { InventoryGroup, InventoryGroupName } from 'src/app/models/results/inventory-group';
import { InventoryQueryParams } from 'src/app/models/results/inventory-query-params';
import { InventoryUserType } from 'src/app/models/results/inventory-user-type';
import { WizardOption } from 'src/app/models/wizard/wizard-option';
import { constants } from 'src/environments/constants';
import { ConfigService } from '../../config/config.service';

@Injectable({
  providedIn: 'root'
})
export class EnterpriseInventoryQueryParamsService implements InventoryQueryParamsService {

  constructor(private confSvc: ConfigService) { }

  setFields(queryParams: InventoryQueryParams, needToRunOptions: WizardOption[], useCaseOptions: WizardOption[], brandOptions: WizardOption[]): void {
    this.setUserType(queryParams, useCaseOptions);
    this.setGroups(queryParams, needToRunOptions);
    this.setBrands(queryParams, brandOptions);

    queryParams.nonParallelsOptionSelected = needToRunOptions.some(option => option.title !== this.confSvc.getString("needToRunParallelsOptionEnterpriseTitle"));
  }

  private setUserType(queryParams: InventoryQueryParams, useCaseOptions: WizardOption[]): void {
    const allUseCases = [
      "useCaseFrontlineWorkerTitle",
      "useCaseContactCenterTitle",
      "useCaseInformationWorkerTitle",
      "useCaseExecutiveWorkerTitle",
      "useCaseKioskTitle",
      "useCaseKioskAndSignageTitle",
      "useCaseSignageTitle",
      "useCaseSharedDevicesTitle",
      "useCaseModernWorkerTitle",
      "useCaseClinicianTitle",
      "useCaseNotListedTitle"
    ];

    console.log("useCaseOptions Length", useCaseOptions.length);

    // If no useCaseOptions or more than 5, include all use cases; otherwise, use the first one
    const selectedUseCases = useCaseOptions.length === 0 || useCaseOptions.length >= 5
      ? allUseCases.map(title => ({ title: this.confSvc.getString(title) } as WizardOption))
      : [useCaseOptions[0]];

    const userType = new InventoryUserType();
    const aggregatedUserType: Partial<InventoryUserType> = {
      name: selectedUseCases.length === 1 ? selectedUseCases[0].title : 'AggregatedUserTypes',
      allowedFormFactors: [],
      minimumScreenSize: undefined,
      maximumScreenSize: undefined,
      minimumStorage: undefined,
      requiresFingerprintSensor: false,
      requiresPrivacyScreen: false,
      requiresBacklitKeyboard: false,
      requiresStylus: false
    };

    selectedUseCases.forEach(useCaseOption => {
      let tempUserType: {
        name: string;
        allowedFormFactors: (string)[];
        minimumScreenSize?: number;
        maximumScreenSize?: number;
        minimumStorage?: number;
        requiresFingerprintSensor?: boolean;
        requiresPrivacyScreen?: boolean;
        requiresBacklitKeyboard?: boolean;
        requiresStylus?: boolean;
      };
      switch (useCaseOption?.title) {
        case this.confSvc.getString("useCaseFrontlineWorkerTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseFrontlineWorkerTitle"),
            allowedFormFactors: [
              constants.CLAMSHELL,
              constants.CONVERTIBLE,
              constants.CHROMEBOX,
              constants.CHROMEBASE,
              constants.TABLET
            ],
            minimumScreenSize: this.confSvc.getNum("frontlineWorkerMinScreenSize"),
            maximumScreenSize: this.confSvc.getNum("frontlineWorkerMaxScreenSize")
          };
          break;
        case this.confSvc.getString("useCaseContactCenterTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseContactCenterTitle"),
            allowedFormFactors: [
              constants.CHROMEBOX,
              constants.CHROMEBOOK,
              constants.CHROMEBOOKPLUS
            ],
            minimumScreenSize: this.confSvc.getNum("informationWorkerMinScreenSize"),
            maximumScreenSize: this.confSvc.getNum("informationWorkerMaxScreenSize"),
            minimumStorage: this.confSvc.getNum("informationWorkerMinStorage"),
            requiresFingerprintSensor: this.confSvc.getBool("informationWorkerRequiresFingerprintSensor"),
            requiresPrivacyScreen: this.confSvc.getBool("informationWorkerRequiresPrivacyScreen"),
            requiresBacklitKeyboard: this.confSvc.getBool("informationWorkerRequiresBacklitKeyboard")
          };
          break;
        case this.confSvc.getString("useCaseInformationWorkerTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseInformationWorkerTitle"),
            allowedFormFactors: [
              constants.CLAMSHELL,
              constants.CONVERTIBLE,
              constants.CHROMEBOX,
              constants.CHROMEBASE
            ],
            minimumScreenSize: this.confSvc.getNum("informationWorkerMinScreenSize"),
            maximumScreenSize: this.confSvc.getNum("informationWorkerMaxScreenSize"),
            minimumStorage: this.confSvc.getNum("informationWorkerMinStorage"),
            requiresFingerprintSensor: this.confSvc.getBool("informationWorkerRequiresFingerprintSensor"),
            requiresPrivacyScreen: this.confSvc.getBool("informationWorkerRequiresPrivacyScreen"),
            requiresBacklitKeyboard: this.confSvc.getBool("informationWorkerRequiresBacklitKeyboard")
          };
          break;
        case this.confSvc.getString("useCaseExecutiveWorkerTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseExecutiveWorkerTitle"),
            allowedFormFactors: [
              constants.CONVERTIBLE
            ],
            minimumScreenSize: this.confSvc.getNum("executiveMobileWorkerMinScreenSize"),
            maximumScreenSize: this.confSvc.getNum("executiveMobileWorkerMaxScreenSize"),
            requiresFingerprintSensor: this.confSvc.getBool("executiveMobileWorkerRequiresFingerprintSensor"),
            requiresPrivacyScreen: this.confSvc.getBool("executiveMobileWorkerRequiresPrivacyScreen"),
            requiresBacklitKeyboard: this.confSvc.getBool("executiveMobileWorkerRequiresBacklitKeyboard"),
            requiresStylus: this.confSvc.getBool("executiveMobileWorkerRequiresStylus")
          };
          break;
        case this.confSvc.getString("useCaseKioskTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseKioskTitle"),
            allowedFormFactors: [
              constants.CHROMEBOX,
              constants.CHROMEBOOK,
              constants.TABLET
            ],
            minimumScreenSize: this.confSvc.getNum("kioskMinScreenSize"),
            maximumScreenSize: this.confSvc.getNum("kioskMaxScreenSize")
          };
          break;
        case this.confSvc.getString("useCaseKioskAndSignageTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseKioskAndSignageTitle"),
            allowedFormFactors: [
              constants.CHROMEBOX,
              constants.CHROMEBOOK,
              constants.TABLET
            ],
            minimumScreenSize: this.confSvc.getNum("kioskMinScreenSize"),
            maximumScreenSize: this.confSvc.getNum("kioskMaxScreenSize")
          };
          break;
        case this.confSvc.getString("useCaseSignageTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseSignageTitle"),
            allowedFormFactors: [
              constants.CHROMEBOX
            ],
            minimumScreenSize: this.confSvc.getNum("signageMinScreenSize"),
            maximumScreenSize: this.confSvc.getNum("signageMaxScreenSize")
          };
          break;
        case this.confSvc.getString("useCaseSharedDevicesTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseSharedDevicesTitle"),
            allowedFormFactors: [
              constants.CHROMEBOX,
              constants.CHROMEBOOK,
              constants.CHROMEBOOKPLUS
            ],
            minimumScreenSize: this.confSvc.getNum("sharedDevicesMinScreenSize"),
            maximumScreenSize: this.confSvc.getNum("sharedDevicesMaxScreenSize")
          };
          break;
        case this.confSvc.getString("useCaseModernWorkerTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseModernWorkerTitle"),
            allowedFormFactors: [
              constants.CHROMEBOX,
              constants.CLAMSHELL,
              constants.CHROMEBOOKPLUS
            ],
            minimumScreenSize: this.confSvc.getNum("modernWorkerMinScreenSize"),
            maximumScreenSize: this.confSvc.getNum("modernWorkerMaxScreenSize")
          };
          break;
        case this.confSvc.getString("useCaseClinicianTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseClinicianTitle"),
            allowedFormFactors: [
              constants.CLAMSHELL,
              constants.CONVERTIBLE,
              constants.CHROMEBOX,
              constants.CHROMEBASE,
              constants.TABLET
            ]
          };
          break;
        case this.confSvc.getString("useCaseNotListedTitle"):
          tempUserType = {
            name: this.confSvc.getString("useCaseNotListedTitle"),
            allowedFormFactors: [
              constants.CLAMSHELL,
              constants.CONVERTIBLE,
              constants.CHROMEBOX,
              constants.CHROMEBASE,
              constants.TABLET
            ]
          };
          break;
        default:
          return;
      }

      // Aggregate properties
      aggregatedUserType.allowedFormFactors = [...new Set([
        ...(aggregatedUserType.allowedFormFactors || []),
        ...(tempUserType.allowedFormFactors || [])
      ])];
      aggregatedUserType.minimumScreenSize = aggregatedUserType.minimumScreenSize !== undefined && tempUserType.minimumScreenSize !== undefined
        ? Math.min(aggregatedUserType.minimumScreenSize, tempUserType.minimumScreenSize)
        : aggregatedUserType.minimumScreenSize ?? tempUserType.minimumScreenSize;
      aggregatedUserType.maximumScreenSize = aggregatedUserType.maximumScreenSize !== undefined && tempUserType.maximumScreenSize !== undefined
        ? Math.max(aggregatedUserType.maximumScreenSize, tempUserType.maximumScreenSize)
        : aggregatedUserType.maximumScreenSize ?? tempUserType.maximumScreenSize;
      aggregatedUserType.minimumStorage = aggregatedUserType.minimumStorage !== undefined && tempUserType.minimumStorage !== undefined
        ? Math.max(aggregatedUserType.minimumStorage, tempUserType.minimumStorage)
        : aggregatedUserType.minimumStorage ?? tempUserType.minimumStorage;
      aggregatedUserType.requiresFingerprintSensor = aggregatedUserType.requiresFingerprintSensor || tempUserType.requiresFingerprintSensor || false;
      aggregatedUserType.requiresPrivacyScreen = aggregatedUserType.requiresPrivacyScreen || tempUserType.requiresPrivacyScreen || false;
      aggregatedUserType.requiresBacklitKeyboard = aggregatedUserType.requiresBacklitKeyboard || tempUserType.requiresBacklitKeyboard || false;
      aggregatedUserType.requiresStylus = aggregatedUserType.requiresStylus || tempUserType.requiresStylus || false;
    });

    Object.assign(userType, aggregatedUserType);
    queryParams.userType = userType;
  }

  private setGroups(queryParams: InventoryQueryParams, needToRunOptions: WizardOption[]): void {
    const needToRunOptionTitles = needToRunOptions.map(option => option.title);

    const groups = this.getGroups(needToRunOptionTitles);

    groups.forEach(group => {
      if (this.needToRunOptionForGroupWasSelected(group, needToRunOptionTitles)) {
        queryParams.groups.push(group);
      }
    });

    this.sortGroupsByPriority(queryParams);
  }

  public getGroups(selectedNeedToRunOptionTitles: string[]): InventoryGroup[] {
    return [
      Object.assign(new InventoryGroup(), {
        name: InventoryGroupName.BrowsingAndSingleTasks,
        minimumRAM: this.confSvc.getNum("simpleTasksMinRam"),
        allowedCPUs: [
          this.confSvc.getString("intelCeleron"),
          this.confSvc.getString("amdA6"),
          this.confSvc.getString("amd"),
          this.confSvc.getString("amdAthlon"),
          this.confSvc.getString("mediatrek500"),
          this.confSvc.getString("intelPentium"),
          this.confSvc.getString("qualcomm7c")
        ],
        priority: this.confSvc.getNum("simpleTasksPriority"),
        needToRunOptions: [this.confSvc.getString("needToRunSimpleTitle")],
        userTypeNames: [
          this.confSvc.getString("useCaseFrontlineWorkerTitle"),
          this.confSvc.getString("useCaseKioskTitle"),
          this.confSvc.getString("useCaseContactCenterTitle"),
          this.confSvc.getString("useCaseNotListedTitle"),
        ]
      }),
      Object.assign(new InventoryGroup(), {
        name: InventoryGroupName.Collaborating,
        minimumRAM: this.confSvc.getNum("advancedTasksMinRam"),
        allowedCPUs: [
          this.confSvc.getString("amdRyzen"),
          this.confSvc.getString("inteli3"),
          this.confSvc.getString("inteli5"),
          this.confSvc.getString("inteli7")
        ],
        priority: this.confSvc.getNum("advancedTasksPriority"),
        needToRunOptions: [
          this.confSvc.getString("needToRunAdvancedTitle"),
        ],
        userTypeNames: [
          this.confSvc.getString("useCaseFrontlineWorkerTitle"),
          this.confSvc.getString("useCaseKioskTitle"),
          this.confSvc.getString("useCaseContactCenterTitle"),
          this.confSvc.getString("useCaseNotListedTitle"),
        ]
      })
    ];
  }

  private needToRunOptionForGroupWasSelected(group: InventoryGroup, selectedNeedToRunOptionTitles: string[]): boolean {
    return group.needToRunOptions.some(option => selectedNeedToRunOptionTitles.includes(option));
  }

  private sortGroupsByPriority(queryParams: InventoryQueryParams): void {
    queryParams.groups.sort((a, b) => (a.priority < b.priority) ? 1 : -1);
  }

  private setBrands(queryParams: InventoryQueryParams, brandOptions: WizardOption[]): void {
    if (brandOptions.length && (brandOptions[0].title !== this.confSvc.getString("brandNoPreferenceOptionTitle"))) {
      queryParams.brands = brandOptions.map(option => this.getDataSetBrandName(option.title));
    }
  }

  private getDataSetBrandName(displayBrandName: string): string {
    return this.confSvc.getRelatedFieldFromConfig(displayBrandName, "Display", "DataSource");
  }
}
